import PageBanner from "@/components/base/PageBanner";
import JsonLd from "@/components/base/JsonLd";

const values = [
  {
    icon: "ri-eye-line",
    title: "更新实时",
    desc: "第一时间整理最新话、今天更新与补更动态，让追更信息始终鲜活。",
  },
  {
    icon: "ri-shield-check-line",
    title: "信息可靠",
    desc: "所有动态均以官方或授权渠道发布为准，只做信息汇总与进度追踪。",
  },
  {
    icon: "ri-user-heart-line",
    title: "读者为本",
    desc: "从追更读者的真实需求出发，把进度与状态做得清晰、直观、好用。",
  },
  {
    icon: "ri-lightbulb-flash-line",
    title: "持续进化",
    desc: "不断打磨产品体验，让66漫画网成为陪伴每一位读者的追更伙伴。",
  },
];

const reviews = [
  {
    name: "追更的老王",
    role: "连载漫画爱好者",
    text: "以前每天要翻好几个地方看更没更新，现在打开66漫画网一眼就看清今天有哪些最新话，省心太多了。",
  },
  {
    name: "夜猫子小林",
    role: "补更收藏家",
    text: "最怕喜欢的漫画断更，现在有断更提醒心里踏实，补更那天还能一次性把漏掉的都补上。",
  },
  {
    name: "学生党阿哲",
    role: "追番学生",
    text: "追更进度条很好用，看到第几话一目了然，随手就能接着看，追更不再错过。",
  },
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于66漫画网",
  url: "https://897kg.cc/about",
  description:
    "66漫画网是一个专注漫画更新动态追踪的中文内容平台，帮助追更读者实时掌握最新话、今天更新、补更与断更等动态。",
  inLanguage: "zh-CN",
};

export default function AboutPage() {
  return (
    <>
      <JsonLd id="jsonld-about" data={aboutSchema} />

      <PageBanner
        eyebrow="关于我们"
        title="关于 66漫画网"
        desc="66漫画网是专注漫画更新动态追踪的追更平台，让每一位读者都能轻松掌握最新话、补更与断更动态。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid items-start gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl text-foreground-900 md:text-3xl">我们为什么存在</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              追连载漫画最怕的，就是错过了某话更新。你兴冲冲点开，却发现停在上一话；想补更，却总也等不到下一话的消息。66漫画网由此而生——我们把散落在各处的更新动态集中起来，用清晰的方式告诉你：
              今天更新了哪几部、哪部出了最新话、哪部正在补更、哪部暂时断更，追更进度一目了然。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              我们相信，追更的快乐不该被「找更新」的麻烦消耗掉。让信息更透明，让进度更清楚，就是66漫画网最朴素也最坚持的事。
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-3 text-sm font-bold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-display text-2xl text-foreground-900 md:text-3xl">读者这样说</h2>
          <p className="mt-2 text-sm text-foreground-600">来自真实追更读者的声音</p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {reviews.map((r) => (
              <figure key={r.name} className="rounded-xl border border-background-200 bg-background-50 p-6">
                <div className="flex items-center gap-1 text-accent-600">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-display text-background-50">
                    {r.name.charAt(0)}
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-foreground-900">{r.name}</span>
                    <span className="block text-xs text-foreground-500">{r.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 p-8 text-center md:p-12">
          <h2 className="font-display text-2xl text-background-50 md:text-3xl">开始你的追更之旅</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-background-100/90 md:text-base">
            从今天起，让66漫画网帮你盯紧每一话更新，追更进度轻松掌握，精彩内容不再错过。
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="mailto:hello@66mangawang.com"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-mail-line"></i>
              联系与合作
            </a>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/60 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-wechat-line"></i>
              微信公众号
            </a>
          </div>
        </div>
      </section>
    </>
  );
}